import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ErrorToast from './ErrorToast';

// App-wide standard error display (DCN-027). Mounted once, near the root
// (see App.js) — every screen calls the same showError(err) instead of
// managing its own formError state / alert(), so "where do I look for
// the error" stops depending on which screen you're on. Always the
// fixed (viewport-corner) ErrorToast variant here, not anchored — a
// global provider has no single page's content box to anchor against.
const ErrorToastContext = createContext(null);

export function ErrorToastProvider({ children }) {
  const [error, setError] = useState(null);
  const location = useLocation();

  // Same fix as ProductPricesPage.js's BrowseNav case, generalized: any
  // navigation can land on a route that reuses an existing component
  // instance (or a brand new one) — either way, an error left over from
  // whatever you were doing on the previous screen shouldn't still be
  // sitting on screen after you've navigated away from it.
  useEffect(() => {
    setError(null);
  }, [location.pathname]);

  // err is an axios error (or anything with a similar .response.data
  // shape) for a real request failure, or omitted/undefined for a
  // client-side-only validation message. fallbackMessage covers both
  // "the request had no response body" and "there was no request at
  // all" cases with one path.
  const showError = useCallback((err, fallbackMessage) => {
    const data = err?.response?.data;
    setError(data && data.message ? data : { message: fallbackMessage || err?.message || 'Something went wrong' });
  }, []);

  const dismissError = useCallback(() => setError(null), []);

  return (
    <ErrorToastContext.Provider value={{ showError, dismissError }}>
      {children}
      <ErrorToast error={error} onDismiss={dismissError} />
    </ErrorToastContext.Provider>
  );
}

// Returns showError(err, fallbackMessage) — call it from any catch block
// instead of setXError(...)/alert(...). Throws if used outside the
// provider (App.js mounts it once, near the root) rather than silently
// no-oping, so a missing provider is caught immediately during
// development, not as a mysteriously-do-nothing button in production.
export function useErrorToast() {
  const ctx = useContext(ErrorToastContext);
  if (!ctx) {
    throw new Error('useErrorToast() must be used within an ErrorToastProvider');
  }
  return ctx.showError;
}

// Opt-in for the rare case a screen needs to clear a toast itself instead of
// waiting on the automatic clear-on-navigate (e.g. a form that stays on the
// same route after a successful save and shouldn't leave a stale validation
// error from an earlier failed attempt sitting on screen).
export function useDismissError() {
  const ctx = useContext(ErrorToastContext);
  if (!ctx) {
    throw new Error('useDismissError() must be used within an ErrorToastProvider');
  }
  return ctx.dismissError;
}
